<template>
    <div v-if="isDelegatedAccess" class="clinic-context">
        <div class="clinic-context__content">
            <div class="clinic-context__icon" aria-hidden="true">
                <v-icon color="primary">mdi-shield-account-outline</v-icon>
            </div>

            <div class="clinic-context__copy">
                <div class="clinic-context__meta">
                    <span class="clinic-context__eyebrow">{{ accessLabel }}</span>
                    <v-chip v-if="currentOwner" x-small color="primary" outlined>
                        Contexto ativo
                    </v-chip>
                </div>

                <template v-if="currentOwner">
                    <span class="clinic-context__message">Você está acessando os dados da clínica</span>
                    <strong>{{ currentOwner.name }}</strong>
                    <small v-if="currentOwner.email">{{ currentOwner.email }}</small>
                </template>

                <template v-else-if="loading">
                    <span class="clinic-context__message">Carregando clínicas disponíveis…</span>
                </template>

                <template v-else>
                    <strong>Selecione uma clínica para visualizar os dados</strong>
                    <small>Nenhuma conta será acessada até você confirmar a escolha.</small>
                </template>
            </div>
        </div>

        <v-btn
            v-if="!loading"
            color="primary"
            outlined
            class="clinic-context__action elevation-0"
            @click="openDialog"
        >
            <v-icon left>{{ currentOwner ? 'mdi-swap-horizontal' : 'mdi-domain' }}</v-icon>
            {{ currentOwner ? 'Trocar clínica' : 'Selecionar clínica' }}
        </v-btn>

        <v-dialog v-model="dialog" max-width="720" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
            <v-card class="clinic-selector">
                <v-card-title class="clinic-selector__header">
                    <div>
                        <span class="eyebrow">Contexto de acesso</span>
                        <h2>Selecionar clínica</h2>
                    </div>
                    <v-btn icon aria-label="Fechar seleção de clínica" @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="clinic-selector__body">
                    <p class="body-sm mb-5">
                        {{ dialogDescription }}
                    </p>

                    <v-text-field
                        v-model="search"
                        label="Buscar por nome ou e-mail"
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        dense
                        clearable
                        hide-details
                        autofocus
                        class="mb-4"
                    />

                    <v-list
                        v-if="filteredOwners.length"
                        class="clinic-selector__list pa-0"
                        role="listbox"
                    >
                        <v-list-item
                            v-for="item in filteredOwners"
                            :key="item._id"
                            link
                            :class="[
                                'clinic-selector__item',
                                String(item._id) === String(pendingOwnerId)
                                    ? 'clinic-selector__item--selected'
                                    : null,
                            ]"
                            :aria-selected="String(item._id) === String(pendingOwnerId)"
                            role="option"
                            @click="pendingOwnerId = String(item._id)"
                        >
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>{{ item.email || 'E-mail não informado' }}</v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                                <div class="clinic-selector__status">
                                    <slot
                                        name="item-status"
                                        :item="item"
                                    >
                                        <v-chip
                                            v-if="typeof item.whatsapp_connected === 'boolean'"
                                            x-small
                                            :color="item.whatsapp_connected ? 'success' : 'grey'"
                                            outlined
                                        >
                                            {{ item.whatsapp_connected ? 'WhatsApp conectado' : 'WhatsApp não conectado' }}
                                        </v-chip>
                                    </slot>
                                    <v-chip
                                        v-if="String(item._id) === String(ownerId)"
                                        x-small
                                        color="primary"
                                        outlined
                                    >
                                        Atual
                                    </v-chip>
                                    <v-icon v-else-if="String(item._id) === String(pendingOwnerId)" color="primary">
                                        mdi-check-circle
                                    </v-icon>
                                </div>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>

                    <empty-state
                        v-else
                        icon="mdi-domain-off"
                        :title="owners.length ? 'Nenhuma clínica encontrada' : 'Nenhuma clínica disponível'"
                        :description="owners.length
                            ? 'Tente buscar por outro nome ou e-mail.'
                            : 'Não há contas de clínica disponíveis para este acesso.'"
                    />
                </v-card-text>

                <v-card-actions class="clinic-selector__actions">
                    <v-spacer />
                    <v-btn text @click="dialog = false">Cancelar</v-btn>
                    <v-btn
                        color="primary"
                        class="elevation-0"
                        :disabled="!pendingOwnerId || String(pendingOwnerId) === String(ownerId)"
                        @click="confirm"
                    >
                        Acessar clínica
                        <v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'ConversationClinicContext',
    props: {
        ownerId: { type: String, default: null },
        owners: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
        dialogDescription: {
            type: String,
            default: 'Escolha a conta cujos atendimentos, demandas e configurações você deseja acessar.',
        },
    },
    data() {
        return {
            dialog: false,
            search: '',
            pendingOwnerId: null,
        }
    },
    computed: {
        currentUser() {
            return this.$store.state.currentUser
        },
        isDelegatedAccess() {
            return ['admin', 'partner'].includes(this.currentUser?.role)
        },
        accessLabel() {
            return this.currentUser?.role === 'admin' ? 'Acesso administrativo' : 'Acesso delegado'
        },
        currentOwner() {
            return this.owners.find(item => String(item._id) === String(this.ownerId)) || null
        },
        filteredOwners() {
            const search = this.normalize(this.search)
            if (!search) return this.owners
            return this.owners.filter(item => (
                this.normalize(`${item.name || ''} ${item.email || ''}`).includes(search)
            ))
        },
    },
    watch: {
        ownerId(value) {
            this.pendingOwnerId = value ? String(value) : null
        },
    },
    methods: {
        normalize(value) {
            return String(value || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .trim()
        },
        openDialog() {
            this.search = ''
            this.pendingOwnerId = this.ownerId ? String(this.ownerId) : null
            this.dialog = true
        },
        confirm() {
            if (!this.pendingOwnerId || String(this.pendingOwnerId) === String(this.ownerId)) return
            const ownerId = String(this.pendingOwnerId)
            this.dialog = false
            this.$emit('change-owner', ownerId)
        },
    },
}
</script>

<style scoped>
.clinic-context {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 12px;
    padding: 18px 20px 18px 24px;
    overflow: hidden;
    color: var(--color-on-surface);
    background: var(--color-surface-container-lowest);
    border: 1px solid rgba(112, 42, 225, 0.32);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-soft);
}

.clinic-context::before {
    position: absolute;
    inset: 0 auto 0 0;
    width: 5px;
    content: '';
    background: var(--color-primary);
}

.clinic-context__content {
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 0;
}

.clinic-context__icon {
    display: grid;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    place-items: center;
    background: rgba(112, 42, 225, 0.12);
    border-radius: 14px;
}

.clinic-context__copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.clinic-context__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 3px;
}

.clinic-context__eyebrow {
    color: var(--color-primary);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .14em;
    text-transform: uppercase;
}

.clinic-context__message,
.clinic-context__copy small {
    color: var(--color-on-surface-variant);
    font-size: 12px;
    line-height: 1.45;
}

.clinic-context__copy strong {
    overflow: hidden;
    font-family: var(--font-display);
    font-size: 18px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.clinic-context__action {
    flex: 0 0 auto;
}

.clinic-selector__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 24px 24px 14px;
}

.clinic-selector__header h2 {
    margin-top: 4px;
    font-family: var(--font-display);
    font-size: 24px;
    line-height: 1.2;
}

.clinic-selector__body {
    padding: 8px 24px 20px !important;
}

.clinic-selector__item {
    min-height: 68px;
    margin-bottom: 6px;
    border: 1px solid var(--color-outline-variant);
    border-radius: var(--radius-sm);
}

.clinic-selector__item--selected {
    background: rgba(112, 42, 225, 0.08);
    border-color: var(--color-primary);
}

.clinic-selector__status {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 5px;
}

.clinic-selector__actions {
    padding: 12px 24px !important;
    border-top: 1px solid var(--color-outline-variant);
}

@media (max-width: 600px) {
    .clinic-context {
        align-items: stretch;
        flex-direction: column;
        padding: 16px 16px 16px 20px;
    }

    .clinic-context__action {
        width: 100%;
    }

    .clinic-selector__body {
        padding-right: 16px !important;
        padding-left: 16px !important;
    }
}
</style>
